import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Production orchestration (part 30).
 *
 * The page is the human half of scripts/deploy-orchestrated.sh. It plans a release, runs the
 * preflight gate and records what the script reports, in the order the API enforces. It cannot
 * start a deploy on its own: every POST carries an Idempotency-Key, the plan is built by the pure
 * Go domain, and a plan with a dependency cycle is refused before it is ever persisted.
 *
 * The blue-green handover and the reverse rollback plan are shown as steps, not hidden behind a
 * single "deploy" button, because the operator needs to see which step is allowed next.
 */

type UnitName = 'db' | 'api' | 'web' | 'edge'
type HealthState = 'healthy' | 'degraded' | 'unhealthy' | 'unknown'
type RunStatus =
  | 'planned'
  | 'preflight_failed'
  | 'ready'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'rolling_back'
  | 'rolled_back'
  | 'rollback_failed'
type StepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped'

interface DeployUnit {
  id: string
  unit: UnitName
  label_th: string
  display_name_th: string
  strategy_label_th: string
  current_version: string
  previous_version?: string
  artifact: string
  endpoint?: string
  health: HealthState
  health_label_th: string
  health_detail_th?: string
  health_checked_at?: string
  last_deployed_at?: string
}

interface PlanStep {
  sequence: number
  unit: UnitName
  action: string
  title_th: string
  detail_th: string
  command: string
  timeout_seconds: number
  critical: boolean
  manual: boolean
  blue_green: boolean
}

interface StepProgress {
  sequence: number
  phase: 'deploy' | 'rollback'
  unit: UnitName
  action: string
  title_th: string
  status: StepStatus
  detail_th?: string
  output?: string
}

interface PreflightCheck {
  key: string
  label_th: string
  status: 'pass' | 'warn' | 'fail'
  required: boolean
  detail_th: string
}

interface DeployRun {
  id: string
  run_key: string
  plan_digest: string
  target: string
  release: string
  status: RunStatus
  status_label_th: string
  units: Array<{ unit: UnitName; version: string; strategy: string; artifact: string }>
  plan: { steps: PlanStep[]; rollback_steps: PlanStep[]; estimated_seconds: number }
  steps: StepProgress[]
  rollback_steps: StepProgress[]
  preflight: { passed: boolean; checks: PreflightCheck[]; blockers: string[]; checked_at?: string }
  requested_by_name?: string
  reason_th?: string
  started_at?: string
  finished_at?: string
  failure_th?: string
  created_at: string
}

interface PlanResponse {
  run: DeployRun
  plan: DeployRun['plan']
  replayed: boolean
}

const runsKey = ['orchestration-runs'] as const
const unitsKey = ['orchestration-units'] as const
const runKey = (id: string) => ['orchestration-run', id] as const

const unitLabel: Record<UnitName, string> = {
  db: 'ฐานข้อมูล',
  api: 'API หลัก',
  web: 'เว็บแอป',
  edge: 'edge',
}

const healthTone: Record<HealthState, string> = {
  healthy: 'sev-low',
  degraded: 'sev-medium',
  unhealthy: 'sev-critical',
  unknown: 'dim',
}

const statusTone: Record<RunStatus, string> = {
  planned: 'dim',
  preflight_failed: 'sev-high',
  ready: 'sev-medium',
  running: 'sev-medium',
  succeeded: 'sev-low',
  failed: 'sev-critical',
  rolling_back: 'sev-high',
  rolled_back: 'sev-low',
  rollback_failed: 'sev-critical',
}

const stepTone: Record<StepStatus, string> = {
  pending: 'dim',
  running: 'sev-medium',
  succeeded: 'sev-low',
  failed: 'sev-critical',
  skipped: 'dim',
}

const stepLabel: Record<StepStatus, string> = {
  pending: 'รอ',
  running: 'กำลังทำ',
  succeeded: 'สำเร็จ',
  failed: 'ล้มเหลว',
  skipped: 'ข้าม',
}

function failureMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback
}

export function Orchestration() {
  const { me } = useAuth()
  const role = me?.role
  const canDeploy = role === 'owner' || role === 'admin' || role === 'audit_manager'
  const queryClient = useQueryClient()

  const [target, setTarget] = useState('production')
  const [release, setRelease] = useState(() => new Date().toISOString().slice(0, 10).replace(/-/g, '.'))
  const [reason, setReason] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  const [stepDetail, setStepDetail] = useState('')
  const [rollbackReason, setRollbackReason] = useState('')

  const units = useQuery({
    queryKey: unitsKey,
    queryFn: () => api<{ units: DeployUnit[]; summary: Record<string, number> }>('/v1/orchestration/units'),
  })
  const runs = useQuery({
    queryKey: runsKey,
    queryFn: () => api<DeployRun[]>('/v1/orchestration/runs?limit=30'),
  })
  const run = useQuery({
    queryKey: runKey(selectedId),
    queryFn: () => api<DeployRun>(`/v1/orchestration/runs/${selectedId}`),
    enabled: Boolean(selectedId),
  })

  useEffect(() => {
    if (!selectedId && runs.data && runs.data.length > 0) setSelectedId(runs.data[0].id)
  }, [runs.data, selectedId])

  const refreshRun = async (id: string) => {
    await queryClient.invalidateQueries({ queryKey: runKey(id) })
    await queryClient.invalidateQueries({ queryKey: runsKey })
    await queryClient.invalidateQueries({ queryKey: unitsKey })
  }

  const plan = useMutation({
    mutationFn: () =>
      apiWrite<PlanResponse>('/v1/orchestration/plans', 'POST', {
        target,
        release,
        reason_th: reason,
      }),
    onSuccess: async (data) => {
      setSelectedId(data.run.id)
      setNotice({
        tone: 'ok',
        text: data.replayed
          ? `พบแผนเดิมของรุ่น ${data.run.release} ระบบคืนงานที่บันทึกไว้แล้ว ไม่ได้สร้างซ้ำ`
          : `สร้างแผนรุ่น ${data.run.release} แล้ว มี ${data.run.plan.steps.length} ขั้นตอน เรียงตามการพึ่งพา`,
      })
      await queryClient.invalidateQueries({ queryKey: runsKey })
    },
    onError: (err) => setNotice({ tone: 'bad', text: failureMessage(err, 'สร้างแผนไม่สำเร็จ') }),
  })

  const preflight = useMutation({
    mutationFn: (id: string) => apiWrite<DeployRun>(`/v1/orchestration/runs/${id}/preflight`, 'POST', {}),
    onSuccess: async (data) => {
      setNotice({
        tone: data.status === 'ready' ? 'ok' : 'bad',
        text:
          data.status === 'ready'
            ? 'ตรวจสภาพผ่านทุกข้อกำหนด เริ่มบันทึกขั้นตอนได้'
            : `ตรวจสภาพไม่ผ่าน ${data.preflight.blockers?.length ?? 0} ข้อ ต้องแก้ก่อนเริ่มติดตั้ง`,
      })
      await refreshRun(data.id)
    },
    onError: (err) => setNotice({ tone: 'bad', text: failureMessage(err, 'ตรวจสภาพไม่สำเร็จ') }),
  })

  const recordStep = useMutation({
    mutationFn: (input: { id: string; step: PlanStep; status: StepStatus; phase: 'deploy' | 'rollback' }) =>
      apiWrite<DeployRun>(`/v1/orchestration/runs/${input.id}/steps`, 'POST', {
        phase: input.phase,
        sequence: input.step.sequence,
        status: input.status,
        detail_th: stepDetail,
      }),
    onSuccess: async (data) => {
      setStepDetail('')
      setNotice({ tone: 'ok', text: `บันทึกผลขั้นตอนแล้ว สถานะงานคือ ${data.status_label_th}` })
      await refreshRun(data.id)
    },
    onError: (err) => setNotice({ tone: 'bad', text: failureMessage(err, 'บันทึกขั้นตอนไม่สำเร็จ') }),
  })

  const rollback = useMutation({
    mutationFn: (id: string) =>
      apiWrite<DeployRun>(`/v1/orchestration/runs/${id}/rollback`, 'POST', { reason_th: rollbackReason }),
    onSuccess: async (data) => {
      setRollbackReason('')
      setNotice({ tone: 'ok', text: 'เริ่มการย้อนกลับแล้ว บันทึกผลแต่ละขั้นตอนตามแผนย้อนกลับ' })
      await refreshRun(data.id)
    },
    onError: (err) => setNotice({ tone: 'bad', text: failureMessage(err, 'เริ่มย้อนกลับไม่สำเร็จ') }),
  })

  const current = run.data
  const rows = runs.data ?? []
  const healthRows = units.data?.units ?? []
  const healthyCount = healthRows.filter((u) => u.health === 'healthy').length
  const activeCount = rows.filter((r) =>
    ['planned', 'preflight_failed', 'ready', 'running', 'rolling_back'].includes(r.status),
  ).length

  const deploySteps = useMemo(() => {
    if (!current) return []
    const progress = new Map(current.steps.map((s) => [s.sequence, s]))
    return current.plan.steps.map((step) => ({
      ...step,
      status: (progress.get(step.sequence)?.status ?? 'pending') as StepStatus,
      result: progress.get(step.sequence),
    }))
  }, [current])

  const rollbackSteps = useMemo(() => {
    if (!current) return []
    const progress = new Map(current.rollback_steps.map((s) => [s.sequence, s]))
    return current.plan.rollback_steps.map((step) => ({
      ...step,
      status: (progress.get(step.sequence)?.status ?? 'pending') as StepStatus,
      result: progress.get(step.sequence),
    }))
  }, [current])

  const nextStep = deploySteps.find((s) => s.status === 'pending' || s.status === 'running')
  const nextRollback = rollbackSteps.find((s) => s.status === 'pending' || s.status === 'running')
  const canRollback = current?.status === 'succeeded' || current?.status === 'failed'
  const canRecordDeploy = current?.status === 'ready' || current?.status === 'running'
  const canRecordRollback = current?.status === 'rolling_back'

  return (
    <>
      <PageHead
        title="จัดลำดับและติดตั้งระบบ"
        sub="วางแผนปล่อยรุ่น ตรวจสภาพก่อนติดตั้ง และบันทึกผลทีละขั้นตอนตามลำดับการพึ่งพา"
      />

      {notice ? (
        <div
          className="card card-pad"
          style={{
            marginBottom: 16,
            borderColor: notice.tone === 'ok' ? 'rgba(46,125,100,0.4)' : 'rgba(166,58,74,0.4)',
          }}
        >
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5 }}>{notice.text}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>ปิดข้อความ</button>
          </div>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat
          label="หน่วยที่สุขภาพปกติ"
          value={healthyCount}
          unit={`/ ${healthRows.length || 4}`}
          foot="จากผลตรวจล่าสุดหรือการติดตั้งที่บันทึกไว้"
        />
        <Stat label="งานที่ยังไม่จบ" value={activeCount} foot="แผน ตรวจสภาพ กำลังติดตั้ง หรือย้อนกลับ" />
        <Stat label="จำนวนรุ่นที่บันทึก" value={rows.length} foot="เรียงจากใหม่ที่สุด" />
        <Stat
          label="ขั้นตอนของแผนที่เลือก"
          value={current?.plan.steps.length ?? 0}
          foot={current ? `ประมาณ ${Math.round(current.plan.estimated_seconds / 60)} นาที` : 'ยังไม่ได้เลือกแผน'}
        />
      </div>

      {units.isLoading ? (
        <Skel h={180} />
      ) : (
        <div className="table-wrap" style={{ marginBottom: 16 }}>
          <table className="table data">
            <thead>
              <tr>
                <th>หน่วยติดตั้ง</th>
                <th>รุ่นที่ให้บริการ</th>
                <th>รูปแบบ</th>
                <th>สุขภาพ</th>
                <th>ตรวจล่าสุด</th>
              </tr>
            </thead>
            <tbody>
              {healthRows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontSize: 12.5 }}>{u.display_name_th}</div>
                    <div className="dim mono" style={{ fontSize: 10.5 }}>{u.unit} · {u.artifact}</div>
                  </td>
                  <td>
                    <div className="mono">{u.current_version || 'ยังไม่มีข้อมูล'}</div>
                    {u.previous_version ? (
                      <div className="dim" style={{ fontSize: 10.5 }}>เก็บรุ่นก่อนหน้า {u.previous_version}</div>
                    ) : null}
                  </td>
                  <td className="dim" style={{ fontSize: 11.5 }}>{u.strategy_label_th}</td>
                  <td>
                    <span className={`sev ${healthTone[u.health]}`} style={{ fontSize: 11 }}>
                      <i className="sev-dot" />
                      {u.health_label_th}
                    </span>
                    {u.health_detail_th ? (
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{u.health_detail_th}</div>
                    ) : null}
                  </td>
                  <td className="dim" style={{ fontSize: 11 }}>
                    {u.health_checked_at ? thaiDateTime(u.health_checked_at) : 'ยังไม่ตรวจ'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ maxWidth: 720 }}>
            <div className="card-label">สร้างแผนติดตั้งรุ่นใหม่</div>
            <p className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 7 }}>
              ระบบเรียงหน่วยตามการพึ่งพาจริง: ฐานข้อมูลก่อน API, API ก่อนเว็บ และเว็บกับ API ก่อน edge
              แผนที่มีวงจรการพึ่งพาจะถูกปฏิเสธ และการวางแผนรุ่นเดิมซ้ำจะคืนงานเดิมแทนการสร้างใหม่
            </p>
          </div>
          {canDeploy ? (
            <button
              className="btn btn-primary"
              disabled={plan.isPending || !release.trim()}
              onClick={() => plan.mutate()}
            >
              {plan.isPending ? 'กำลังสร้างแผน…' : 'สร้างแผนติดตั้ง'}
            </button>
          ) : (
            <span className="chip">บทบาทของคุณสร้างแผนไม่ได้</span>
          )}
        </div>

        <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11.5 }}>
            <div className="dim" style={{ marginBottom: 5 }}>สภาพแวดล้อม</div>
            <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="production">production</option>
              <option value="staging">staging</option>
            </select>
          </label>
          <label style={{ fontSize: 11.5 }}>
            <div className="dim" style={{ marginBottom: 5 }}>ชื่อรุ่น</div>
            <input
              className="input"
              value={release}
              onChange={(e) => setRelease(e.target.value)}
              placeholder="2026.11.0"
              style={{ width: 180 }}
            />
          </label>
          <label style={{ fontSize: 11.5, flex: '1 1 320px' }}>
            <div className="dim" style={{ marginBottom: 5 }}>เหตุผลการเปลี่ยนแปลง (จำเป็นบน production)</div>
            <input
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ปิดช่องโหว่การเข้าถึงและปรับดัชนีรายงาน"
              style={{ width: '100%' }}
            />
          </label>
        </div>
      </div>

      <div className="grid-main">
        <div>
          {runs.isLoading ? (
            <Skel h={360} />
          ) : !current ? (
            <Empty
              title="ยังไม่มีแผนติดตั้ง"
              body="สร้างแผนเพื่อดูลำดับขั้นตอน ตรวจสภาพก่อนติดตั้ง และบันทึกผลการปล่อยรุ่น"
            />
          ) : (
            <div className="stack">
              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div className="mono gold" style={{ fontSize: 11 }}>{current.run_key.slice(0, 16)}…</div>
                    <h2 className="heading-m" style={{ marginTop: 6 }}>รุ่น {current.release}</h2>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 5 }}>
                      {current.target} · ผู้วางแผน {current.requested_by_name || 'ไม่ระบุ'} · {thaiDateTime(current.created_at)}
                    </div>
                  </div>
                  <span className={`sev ${statusTone[current.status]}`}>
                    <i className="sev-dot" />
                    {current.status_label_th}
                  </span>
                </div>

                <div className="row" style={{ marginTop: 14, gap: 8, flexWrap: 'wrap' }}>
                  <span className="chip">เรียงหน่วย {current.units.map((u) => u.unit).join(' → ')}</span>
                  <span className="chip">ประมาณ {Math.round(current.plan.estimated_seconds / 60)} นาที</span>
                  {(current.preflight.checks?.length ?? 0) > 0 ? (
                    <span className="chip">
                      ตรวจสภาพ {current.preflight.passed ? 'ผ่าน' : `ไม่ผ่าน ${current.preflight.blockers?.length ?? 0} ข้อ`}
                    </span>
                  ) : (
                    <span className="chip">ยังไม่ได้ตรวจสภาพ</span>
                  )}
                </div>

                {current.failure_th ? (
                  <div className="demo-note" style={{ marginTop: 12 }}>{current.failure_th}</div>
                ) : null}

                <div className="row" style={{ marginTop: 14, gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={!canDeploy || preflight.isPending || !['planned', 'preflight_failed', 'ready'].includes(current.status)}
                    onClick={() => preflight.mutate(current.id)}
                  >
                    {preflight.isPending ? 'กำลังตรวจสภาพ…' : 'ตรวจสภาพก่อนติดตั้ง'}
                  </button>
                  {canRollback && canDeploy ? (
                    <button
                      className="btn btn-sm"
                      disabled={rollback.isPending || !rollbackReason.trim()}
                      onClick={() => rollback.mutate(current.id)}
                    >
                      เริ่มย้อนกลับ
                    </button>
                  ) : null}
                  {current.status === 'rolling_back' ? (
                    <span className="chip">กำลังย้อนกลับ · เหลือ {rollbackSteps.filter((s) => s.status === 'pending' || s.status === 'running').length} ขั้นตอน</span>
                  ) : null}
                </div>

                {canRollback ? (
                  <input
                    className="input"
                    value={rollbackReason}
                    onChange={(e) => setRollbackReason(e.target.value)}
                    placeholder="เหตุผลการย้อนกลับ (จำเป็น)"
                    style={{ width: '100%', marginTop: 10 }}
                  />
                ) : null}
              </div>

              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="card-label" style={{ margin: 0 }}>ขั้นตอนติดตั้งตามลำดับ</span>
                  {nextStep ? <span className="chip">ขั้นถัดไป #{nextStep.sequence}</span> : <span className="chip">ครบทุกขั้นตอน</span>}
                </div>

                <div className="table-wrap">
                  <table className="table data">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        <th>ขั้นตอน</th>
                        <th>คำสั่งที่สคริปต์ใช้</th>
                        <th>สถานะ</th>
                        <th>บันทึกผล</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deploySteps.map((step) => {
                        const isNext = nextStep?.sequence === step.sequence
                        return (
                          <tr key={step.sequence}>
                            <td className="num mono">{step.sequence}</td>
                            <td style={{ maxWidth: 300 }}>
                              <div style={{ fontSize: 12.5 }}>{step.title_th}</div>
                              <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{step.detail_th}</div>
                              <div className="row" style={{ gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                                <span className="chip" style={{ fontSize: 9.5 }}>{unitLabel[step.unit]}</span>
                                {step.blue_green ? <span className="chip gold" style={{ fontSize: 9.5 }}>สีเขียว-น้ำเงิน</span> : null}
                                {step.manual ? <span className="chip" style={{ fontSize: 9.5 }}>ด้วยมือ</span> : null}
                                {step.critical ? <span className="chip" style={{ fontSize: 9.5 }}>จำเป็น</span> : null}
                              </div>
                            </td>
                            <td className="mono dim" style={{ fontSize: 10, maxWidth: 240, wordBreak: 'break-word' }}>{step.command}</td>
                            <td>
                              <span className={`sev ${stepTone[step.status]}`} style={{ fontSize: 11 }}>
                                <i className="sev-dot" />
                                {stepLabel[step.status]}
                              </span>
                              {step.result?.detail_th ? (
                                <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{step.result.detail_th}</div>
                              ) : null}
                            </td>
                            <td>
                              {canDeploy && canRecordDeploy && isNext ? (
                                <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                                  {step.status === 'pending' ? (
                                    <button
                                      className="btn btn-sm"
                                      disabled={recordStep.isPending}
                                      onClick={() => recordStep.mutate({ id: current.id, step, status: 'running', phase: 'deploy' })}
                                    >
                                      เริ่ม
                                    </button>
                                  ) : null}
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={recordStep.isPending}
                                    onClick={() => recordStep.mutate({ id: current.id, step, status: 'succeeded', phase: 'deploy' })}
                                  >
                                    สำเร็จ
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    disabled={recordStep.isPending}
                                    onClick={() => recordStep.mutate({ id: current.id, step, status: 'failed', phase: 'deploy' })}
                                  >
                                    ล้มเหลว
                                  </button>
                                </div>
                              ) : (
                                <span className="dim" style={{ fontSize: 10.5 }}>—</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                  <input
                    className="input"
                    value={stepDetail}
                    onChange={(e) => setStepDetail(e.target.value)}
                    placeholder="หมายเหตุผลของขั้นตอน (จะถูกบันทึกในหลักฐาน)"
                    style={{ flex: '1 1 320px' }}
                  />
                  <span className="dim" style={{ fontSize: 10.5 }}>บันทึกได้เฉพาะขั้นถัดไปตามลำดับ</span>
                </div>
              </div>

              {(current.preflight.checks?.length ?? 0) > 0 ? (
                <div className="card card-pad">
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="card-label" style={{ margin: 0 }}>ผลตรวจสภาพก่อนติดตั้ง</span>
                    <span className="chip">{current.preflight.passed ? 'ผ่าน' : 'ไม่ผ่าน'}</span>
                  </div>
                  <div className="stack" style={{ gap: 8 }}>
                    {current.preflight.checks.map((check) => (
                      <div key={check.key} className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                        <span
                          className={`sev ${check.status === 'pass' ? 'sev-low' : check.status === 'warn' ? 'sev-medium' : 'sev-critical'}`}
                          style={{ fontSize: 11, flex: '0 0 90px' }}
                        >
                          <i className="sev-dot" />
                          {check.status === 'pass' ? 'ผ่าน' : check.status === 'warn' ? 'เตือน' : 'ไม่ผ่าน'}
                        </span>
                        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                          <div>{check.label_th}{check.required ? ' · จำเป็น' : ''}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>{check.detail_th}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {current.status === 'rolling_back' || current.status === 'rolled_back' || current.status === 'rollback_failed' ? (
                <div className="card card-pad">
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="card-label" style={{ margin: 0 }}>แผนย้อนกลับตามลำดับย้อนกลับ</span>
                    <span className="chip">{rollbackSteps.filter((s) => s.status === 'succeeded' || s.status === 'skipped').length}/{rollbackSteps.length}</span>
                  </div>
                  <div className="table-wrap">
                    <table className="table data">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th>ขั้นตอน</th>
                          <th>สถานะ</th>
                          <th>บันทึกผล</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rollbackSteps.map((step) => {
                          const isNext = nextRollback?.sequence === step.sequence
                          return (
                            <tr key={step.sequence}>
                              <td className="num mono">{step.sequence}</td>
                              <td>
                                <div style={{ fontSize: 12.5 }}>{step.title_th}</div>
                                <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{step.detail_th}</div>
                              </td>
                              <td>
                                <span className={`sev ${stepTone[step.status]}`} style={{ fontSize: 11 }}>
                                  <i className="sev-dot" />
                                  {stepLabel[step.status]}
                                </span>
                              </td>
                              <td>
                                {canDeploy && canRecordRollback && isNext ? (
                                  <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      disabled={recordStep.isPending}
                                      onClick={() => recordStep.mutate({ id: current.id, step, status: 'succeeded', phase: 'rollback' })}
                                    >
                                      สำเร็จ
                                    </button>
                                    {step.manual ? (
                                      <button
                                        className="btn btn-sm"
                                        disabled={recordStep.isPending || !stepDetail.trim()}
                                        onClick={() => recordStep.mutate({ id: current.id, step, status: 'skipped', phase: 'rollback' })}
                                      >
                                        ข้ามพร้อมเหตุผล
                                      </button>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="dim" style={{ fontSize: 10.5 }}>—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {nextRollback?.manual ? (
                    <div className="demo-note" style={{ marginTop: 12 }}>
                      ขั้นฐานข้อมูลเป็นขั้นที่ต้องตัดสินใจด้วยมือ: ระบบจะไม่ย้อนสคีมาอัตโนมัติ
                      ต้องกู้จากไฟล์สำรองที่อนุมัติแล้วและใส่เหตุผลก่อนข้าม
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <span className="card-label" style={{ margin: 0 }}>งานติดตั้งล่าสุด</span>
              <span className="chip">{rows.length}</span>
            </div>
            {runs.isLoading ? (
              <Skel h={200} />
            ) : rows.length === 0 ? (
              <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีงานติดตั้ง</div>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {rows.map((item) => (
                  <button
                    key={item.id}
                    className="card card-pad"
                    style={{
                      textAlign: 'left',
                      padding: 12,
                      font: 'inherit',
                      color: 'inherit',
                      borderColor: item.id === selectedId ? 'rgba(200,169,106,0.4)' : undefined,
                    }}
                    onClick={() => { setSelectedId(item.id); setNotice(null) }}
                  >
                    <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                      <span className="mono" style={{ fontSize: 11.5 }}>{item.release}</span>
                      <span className={`sev ${statusTone[item.status]}`} style={{ fontSize: 10.5 }}>
                        <i className="sev-dot" />
                        {item.status_label_th}
                      </span>
                    </div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>
                      {item.target} · {thaiDateTime(item.created_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label">ขอบเขตของหน้านี้</div>
            <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.75, marginTop: 8 }}>
              หน้านี้ไม่เรียกคำสั่งติดตั้งเอง ทุกครั้งที่บันทึกผลคือการรายงานของ
              scripts/deploy-orchestrated.sh ที่อ่านแผนนี้และรันคำสั่งจริงบนเครื่องผู้ดูแล
              การย้อนกลับของฐานข้อมูลเป็นขั้นที่ต้องยืนยันด้วยมือเสมอ
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default Orchestration
